import React, { useState, useContext, useEffect, useRef } from "react";
import { Paper, Typography, IconButton, Tooltip, Stack, TableContainer, TableBody, TableCell, TableHead, TableRow, Table, Button, Box, Grid } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";

import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import { isAccess, simpleMessage } from "../../../helpers/Helpers";

import moment from "moment";
import { DataContext } from "../../../context/DataContext";
import { isEmpty } from "lodash";
import NoData from "../../../components/NoData";
import { getCreditPaymentAsync, getMontoNotasAsync, getSaldoByTipoAbonoAsync } from "../../../api/Credits";
import { getToken } from "../../../api/Account";
import ReactToPrint from "react-to-print";
import PrintPayment from "./PrintPayment";
import SmallModal from "../../../components/modals/SmallModal";
import UpdateFechaPago from "./paymentComponents/UpdateFechaPago";
import PrintPagare from "./paymentComponents/PrintPagare";
import StatusChip from "../../../components/ui/StatusChip";
import { monoFont, lightTheme } from "../../../theme";

const money = (n) => new Intl.NumberFormat("es-NI", { style: "currency", currency: "NIO" }).format(n);

// Estado visual derivado de datos reales (isCanceled + fechaPago) — no se inventa un campo nuevo.
const estadoCuota = (row, esProximaPendiente) => {
  if (row.isCanceled) {
    return { label: "Pagada", tone: "success" };
  }
  const vencida = moment(row.fechaPago).isBefore(moment(), "day");
  if (vencida) {
    return { label: "Vencida", tone: "error" };
  }
  return { label: esProximaPendiente ? "Próxima" : "Pendiente", tone: esProximaPendiente ? "info" : "neutral" };
};

const CreditPayment = ({ id, clientName, isNominal, dataCredit }) => {
  const token = getToken();
  const { setIsLoading, setIsUnautorized, access } = useContext(DataContext);

  const [paymentList, setPaymentList] = useState([]);
  const [credit, setCredit] = useState([]);
  const [quoteList, setQuoteList] = useState([]);

  const [montoPagar, setMontoPagar] = useState();
  const [montoNotas, setMontoNotas] = useState();
  const [saldoPendiente, setSaldoPendiente] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState([]);
  const [localReload, setLocalReload] = useState(false);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const result = await getCreditPaymentAsync(token, id);
      if (!result.statusResponse) {
        setIsLoading(false);
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        simpleMessage(result.error, "error");
        return;
      }
      setPaymentList(result.data.result.payments);

      setCredit(result.data.result);
      setQuoteList(result.data.result.quotes);

      setMontoPagar(result.data.result.montoPagar);

      getMontoNotas();
      getSaldoPendiente();

      setIsLoading(false);
    })();
  }, [localReload]);

  const getSaldoPendiente = async () => {
    const result = await getSaldoByTipoAbonoAsync(token, { creditId: id, tipoAbono: 1 });
    if (!result.statusResponse) {
      if (result.error === "Unauthorized") {
        setIsUnautorized(true);
        return;
      }
      simpleMessage(result.error, "error");
      return;
    }
    setSaldoPendiente(result.data.result);
  };

  const totalAbonado = () => {
    let result = 0;
    if (quoteList.length > 0) {
      quoteList.map((item) => (result += item.totalRecibido));
    }
    return result;
  };

  const compRefPagare = useRef();
  const compRef = useRef();

  const pageStyle = `
  @media print {
    html, body {
      height: initial !important;
      overflow: initial !important;
      -webkit-print-color-adjust: exact;
    }
  }
  @media all {
    .pagebreak {
      display: none;
    }
  }
  @media print {
    .pagebreak {
      page-break-before: always;
    }
  }
  @page {   margin: 8mm; }
`;

  const pageStylePagare = `
  @media print {
    html, body {
      height: initial !important;
      overflow: initial !important;
      -webkit-print-color-adjust: exact;
    }
  }
  @media all {
    .pagebreak {
      display: none;
    }
  }
  @media print {
    .pagebreak {
      page-break-before: always;
    }
  }
  // @page { size: landscape;  margin: 8mm; }
`;

  const editpayment = async (payment) => {
    setSelectedPayment(payment);
    setShowModal(true);
  };

  const getMontoNotas = async (value) => {
    const result = await getMontoNotasAsync(token, id);
    if (!result.statusResponse) {
      setIsLoading(false);
      if (result.error === "Unauthorized") {
        setIsUnautorized(true);
        return;
      }
      simpleMessage(result.error, "error");
      return;
    }
    setIsLoading(false);
    setMontoNotas(result.data.result);
  };

  const primeraPendienteIndex = paymentList.findIndex((r) => !r.isCanceled);

  return (
    <div>
      <Stack direction="column" spacing={1.75}>
        <Paper sx={{ overflow: "hidden" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "16px 20px",
              borderBottom: "1px solid",
              borderColor: "divider",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            <Typography sx={{ fontSize: 15, fontWeight: 600 }}>Plan de pagos</Typography>

            <Stack direction="row" spacing={1.1}>
              {isAccess(access, "PRINT PAGARE") ? (
                <ReactToPrint
                  trigger={() => (
                    <Button variant="outlined" color="success" size="small" startIcon={<PrintRoundedIcon sx={{ fontSize: 17 }} />}>
                      Contrato
                    </Button>
                  )}
                  content={() => compRefPagare.current}
                  pageStyle={pageStylePagare}
                />
              ) : null}

              <ReactToPrint
                trigger={() => (
                  <Button variant="outlined" size="small" startIcon={<PrintRoundedIcon sx={{ fontSize: 17 }} />}>
                    Plan de pagos
                  </Button>
                )}
                content={() => compRef.current}
                pageStyle={pageStyle}
              />
            </Stack>
          </Box>

          {isEmpty(paymentList) ? (
            <NoData />
          ) : (
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 1080 }}>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">#Cuota</TableCell>
                    <TableCell align="center">Días</TableCell>
                    <TableCell align="center">F.Pago</TableCell>
                    <TableCell align="right">S.Anterior</TableCell>
                    <TableCell align="right">C.Principal</TableCell>
                    <TableCell align="right">C.Interés C.</TableCell>
                    <TableCell align="right">C.Seguro</TableCell>
                    <TableCell align="right">C.Pagar</TableCell>
                    <TableCell align="right">Nuevo S.</TableCell>
                    <TableCell align="center">Estado</TableCell>
                    {isAccess(access, "PAYMENT UPDATE") ? <TableCell align="center">Acciones</TableCell> : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentList.map((row, index) => {
                    const estado = estadoCuota(row, index === primeraPendienteIndex);
                    const rowBg =
                      estado.label === "Vencida"
                        ? "error.light"
                        : estado.label === "Próxima"
                        ? "success.light"
                        : "transparent";

                    return (
                      <TableRow key={row.id} sx={{ backgroundColor: rowBg }}>
                        <TableCell align="center" sx={{ fontFamily: monoFont, fontSize: 11.5, color: "text.disabled" }}>
                          {row.numeroCuota}
                        </TableCell>
                        <TableCell align="center" sx={{ fontFamily: monoFont, fontSize: 11.5 }}>
                          {row.periodicidad}
                        </TableCell>
                        <TableCell align="center" sx={{ fontFamily: monoFont, fontSize: 11.5 }}>
                          {moment(row.fechaPago).format("L")}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: monoFont, fontSize: 11.5, color: "text.secondary" }}>
                          {money(row.saldoAnterior)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: monoFont, fontSize: 11.5 }}>
                          {money(row.montoPrincipal)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: monoFont, fontSize: 11.5 }}>
                          {money(row.montoInteresCorriente)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: monoFont, fontSize: 11.5 }}>
                          {money(row.montoSeguro)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: monoFont, fontSize: 12, fontWeight: 600 }}>
                          {money(isNominal ? row.montoInteresCorriente : row.cuotaPrincipal)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: monoFont, fontSize: 11.5 }}>
                          {money(row.nuevoSaldo)}
                        </TableCell>
                        <TableCell align="center">
                          <StatusChip label={estado.label} tone={estado.tone} />
                        </TableCell>

                        {isAccess(access, "PAYMENT UPDATE") ? (
                          <TableCell align="center">
                            <Tooltip title="Editar fecha de pago">
                              <IconButton size="small" sx={{ color: "warning.main" }} onClick={() => editpayment(row)}>
                                <EventRoundedIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        ) : null}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Grid container spacing={1.75}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ padding: "16px 18px" }}>
              <Typography sx={{ fontSize: 11.5, color: "text.secondary", fontWeight: 500, marginBottom: "7px" }}>Monto a pagar</Typography>
              <Typography sx={{ fontFamily: monoFont, fontSize: 18, fontWeight: 600 }}>{money(montoPagar)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ padding: "16px 18px" }}>
              <Typography sx={{ fontSize: 11.5, color: "text.secondary", fontWeight: 500, marginBottom: "7px" }}>Total abonado</Typography>
              <Typography sx={{ fontFamily: monoFont, fontSize: 18, fontWeight: 600, color: "success.main" }}>{money(totalAbonado())}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ padding: "16px 18px" }}>
              <Typography sx={{ fontSize: 11.5, color: "text.secondary", fontWeight: 500, marginBottom: "7px" }}>Total notas de crédito</Typography>
              <Typography sx={{ fontFamily: monoFont, fontSize: 18, fontWeight: 600, color: "success.main" }}>{money(montoNotas)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ padding: "16px 18px" }}>
              <Typography sx={{ fontSize: 11.5, color: "text.secondary", fontWeight: 500, marginBottom: "7px" }}>Saldo pendiente</Typography>
              <Typography sx={{ fontFamily: monoFont, fontSize: 18, fontWeight: 600, color: "error.main" }}>
                {money(saldoPendiente)}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Stack>

      <div style={{ display: "none" }}>
        <ThemeProvider theme={lightTheme}>
          <PrintPagare clientName={clientName} dataCredit={dataCredit} paymentList={paymentList} ref={compRefPagare} />
        </ThemeProvider>
      </div>

      {credit.length === 0 ? null : (
        <div style={{ display: "none" }}>
          <ThemeProvider theme={lightTheme}>
            <PrintPayment clientName={clientName} credit={credit} paymentList={paymentList} ref={compRef} address={dataCredit.client.address} dataCredit={dataCredit} />
          </ThemeProvider>
        </div>
      )}

      {isAccess(access, "PAYMENT UPDATE") ? (
        <SmallModal titulo={`Editar Fecha de Pago`} isVisible={showModal} setVisible={setShowModal}>
          <UpdateFechaPago data={selectedPayment} setShowModal={setShowModal} localReload={localReload} setLocalReload={setLocalReload} />
        </SmallModal>
      ) : null}
    </div>
  );
};

export default CreditPayment;
